import { useCallback, useState } from "react";

// import { LOCATION_STATES, ROUTES } from "library/constants";
import { isBefore, isEqual, isSameDay } from "date-fns";
import { ICardBenefitPackage } from "library/types";
import { useTranslation } from "react-i18next";

import { ButtonWithTooltip } from "components/ButtonWithTooltip/ButtonWithTooltip";
import { useAppTheme } from "hooks/useAppTheme";
import useEmployee from "hooks/useEmployee";

import { CancelEnrolledPackage } from "./CancelEnrolledPackage";
import { EnrollPackageDialog } from "./EnrollPackageDialog/EnrollPackageDialog";

import { ActionWrap } from "../style-new";

type Props = {
  benefitPackage?: ICardBenefitPackage;
  eligible?: boolean;
  waitingForApproval?: boolean;
  waitingForCancel?: boolean;
  packageFormSameImplementationsInPendingStatus?: string;
  upgradeState?: {
    enrolledPackageName: string;
    upgradePackageName: string;
  };
  packageIsExpired?: boolean;
  withShadow?: boolean;
};

export const BenefitAction = ({
  eligible,
  waitingForApproval,
  waitingForCancel,
  packageFormSameImplementationsInPendingStatus,
  upgradeState,
  benefitPackage,
  packageIsExpired,
  withShadow,
}: Props) => {
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [cancelEnrollOpen, setCancelEnrollOpen] = useState(false);

  const { t } = useTranslation();

  const { employeeData } = useEmployee();

  const theme = useAppTheme();
  const addNewLabel = t("hbh.enrollPackage.label");

  const isGrECoSensitiveDataConsentGiven = employeeData?.consents?.some(
    (consent) => !!consent.isSensitiveDataConsent
  );

  const packageFromSameImplementationInPendingStatusLabel = t(
    "hbh.packageFormSameImplementationsInPendingStatus.label",
    {
      packageName: packageFormSameImplementationsInPendingStatus,
    }
  );
  const disabledCancelLabel = t("hbh.disabledCancelPackage.label");

  const upgradeLabel = t("hbh.upgradePackage.label", {
    enrolledPackageName: upgradeState?.enrolledPackageName,
    upgradePackageName: upgradeState?.upgradePackageName,
  });

  const cancelEnrollmentDisabled = (() => {
    const cancellationPossibility = benefitPackage?.cancellationPossibility;
    const validTo = benefitPackage?.validTo;

    if (!cancellationPossibility || !validTo) return !!packageIsExpired;

    const cancelDate = new Date(cancellationPossibility);
    const validToDate = new Date(validTo);


    const isDateBefore = isBefore(validToDate,cancelDate);
    const isDateEqual = isSameDay(cancelDate, validToDate);

    return isDateBefore || isDateEqual || !!packageIsExpired;
  })();

  const dateOfCancellation = benefitPackage?.dateOfCancellation;
  const deleteLabel = t("hbh.cancelPackage.label");



  const setEnrollOpenState = useCallback((state) => {
    setEnrollOpen(state);
  }, []);

  const setCancelEnrollOpenState = useCallback((state) => {
    setCancelEnrollOpen(state);
  }, []);

  return (
    <>
      {eligible && !waitingForApproval && !waitingForCancel ? (
        <ActionWrap $isAdd={!!eligible} $withShadow={withShadow}>
          <ButtonWithTooltip
            data-help="benefits-card-enroll"
            disabled={!!packageFormSameImplementationsInPendingStatus}
            tooltipContent={(() => {
              if (packageFormSameImplementationsInPendingStatus) {
                return packageFromSameImplementationInPendingStatusLabel;
              }
              if (upgradeLabel) {
                return upgradeLabel;
              }

              return addNewLabel;
            })()}
            handleClick={() => {
              setEnrollOpenState(true);
            }}
            style={{
              ...(packageFormSameImplementationsInPendingStatus
                ? {}
                : { color: theme.palette.white }),
            }}
            // icon={
            //   upgradeState ? <Rocket24Regular /> : <GiftOpen24Regular />
            // }
          >
            {t(upgradeState ? "hbh.upgrade.label" : "hbh.enroll.label")}
          </ButtonWithTooltip>
        </ActionWrap>
      ) : null}

      {!eligible && !dateOfCancellation && !cancelEnrollmentDisabled ? (
        <ActionWrap $isAdd={!!eligible}>
          <ButtonWithTooltip
            data-help="benefits-card-cancel"
            disabled={cancelEnrollmentDisabled}
            tooltipContent={cancelEnrollmentDisabled ? disabledCancelLabel : deleteLabel}
            handleClick={() => {
              setCancelEnrollOpenState(true);
            }}
            style={{
              ...(cancelEnrollmentDisabled ? {} : { color: theme.palette.red }),
            }}
            // icon={<DismissFilled />}
          >
            {t("greco.cancel")}
          </ButtonWithTooltip>
        </ActionWrap>
      ) : null}

      {enrollOpen && benefitPackage && employeeData && (
        <EnrollPackageDialog
          isUpgradeEnroll={!!upgradeState}
          open={enrollOpen}
          setOpen={setEnrollOpenState}
          isGrECoSensitiveDataConsentGiven={isGrECoSensitiveDataConsentGiven}
          benefitPackage={benefitPackage}
        />
      )}

      {cancelEnrollOpen && benefitPackage && (
        <CancelEnrolledPackage
          open={cancelEnrollOpen}
          setOpen={setCancelEnrollOpenState}
          benefitPackage={benefitPackage}
        />
      )}
    </>
  );
};
